import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import styles from './HowToChoosePage.module.css';

const VALID_CATEGORIES = [
  'boots',
  'crampons',
  'iceAxes',
  'backpacks',
  'hardshell',
  'softshell',
  'midlayer',
  'baseLayer',
  'other',
  'trekkingPoles',
  'headlamps',
  'location',
  'climbing'
];

// Map URL slugs to translation keys
const CATEGORY_MAP = {
  'boots': 'boots',
  'crampons': 'crampons',
  'ice-axes': 'iceAxes',
  'backpacks': 'backpacks',
  'hardshell': 'hardshell',
  'softshell': 'softshell',
  'midlayer': 'midlayer',
  'base-layer': 'baseLayer',
  'other': 'other',
  'trekking-poles': 'trekkingPoles',
  'headlamps': 'headlamps',
  'location': 'location',
  'climbing': 'climbing'
};

// Map categories to gear page tab IDs for "back to gear" link
const CATEGORY_TO_TAB = {
  'boots': 'boots',
  'crampons': 'crampons_axes',
  'iceAxes': 'crampons_axes',
  'backpacks': 'backpacks',
  'hardshell': 'hardshell',
  'softshell': 'softshell',
  'midlayer': 'midlayer',
  'baseLayer': 'baselayers',
  'other': 'accessories',
  'trekkingPoles': 'poles',
  'headlamps': 'electronics',
  'location': 'electronics',
  'climbing': 'climbing'
};

function HowToChoosePage() {
  const { category: urlCategory } = useParams();
  const { t } = useTranslation(['howto', 'translation']);

  const category = CATEGORY_MAP[urlCategory];
  
  if (!category || !VALID_CATEGORIES.includes(category)) {
    return (
      <div className={styles.pageContainer}>
        <div className={styles.notFound}>
          <h1>Guide Not Found</h1>
          <p>The gear guide you're looking for doesn't exist.</p>
          <Link to="/gear" className={styles.backLink}>← Back to Gear</Link>
        </div>
      </div>
    );
  }

  const title = t(`${category}.title`);
  const metaTitle = t(`${category}.metaTitle`);
  const metaDescription = t(`${category}.metaDescription`);
  const intro = t(`${category}.intro`);
  const sections = t(`${category}.sections`, { returnObjects: true });
  const tabId = CATEGORY_TO_TAB[category];

  // Helper to map 'howto' keys to 'translation' keys for back link
  const translateCategoryKey = (cat) => {
    const map = {
      'boots': 'Boots',
      'crampons': 'Crampons',
      'iceAxes': 'Ice axes',
      'backpacks': 'Backpacks',
      'hardshell': 'Hardshell',
      'softshell': 'Softshell',
      'midlayer': 'Midlayer',
      'baseLayer': 'Base layers',
      'other': 'Accessories',
      'trekkingPoles': 'Trekking poles',
      'headlamps': 'Head lamps',
      'location': 'Location',
      'climbing': 'Climbing gear'
    };
    return map[cat] || 'Midlayer'; 
  };

  const getParentCategory = (cat) => {
    const map = {
      'boots': 'Footwear',
      'crampons': 'Alpinism',
      'iceAxes': 'Alpinism',
      'backpacks': 'Equipment',
      'hardshell': 'Clothing',
      'softshell': 'Clothing',
      'midlayer': 'Clothing',
      'baseLayer': 'Clothing',
      'other': 'Accessories',
      'trekkingPoles': 'Equipment',
      'headlamps': 'Electronics',
      'location': 'Electronics',
      'climbing': 'Alpinism'
    };
    return map[cat] || 'Equipment';
  };

  return (
    <>
      <Helmet>
        <title>{metaTitle} | Peaks on Foot</title>
        <meta name="description" content={metaDescription} />
        <meta property="og:title" content={metaTitle} />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:type" content="article" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={metaTitle} />
        <meta name="twitter:description" content={metaDescription} />
        <link rel="canonical" href={`https://peaksonfoot.com/gear/guide/${urlCategory}`} />
      </Helmet>

      <div className={styles.pageContainer}>
        <div className={styles.breadcrumb}>
          <Link to="/gear" className={styles.breadcrumbLink}>{t('gear.title', 'Gear')}</Link>
          <span className={styles.breadcrumbSeparator}>/</span>
          <Link to="/gear" className={styles.breadcrumbLink}>{getParentCategory(category)}</Link>
          <span className={styles.breadcrumbSeparator}>/</span>
          <span className={styles.breadcrumbCurrent}>{title}</span>
        </div>

        <article className={styles.article}>
          <header className={styles.header}>
            <h1 className={styles.title}>{title}</h1>
            <p className={styles.intro}>{intro}</p>
          </header>

          <div className={styles.content}>
            {sections && typeof sections === 'object' && Object.entries(sections).map(([key, section]) => (
              <section key={key} className={styles.section}>
                <h2 className={styles.sectionTitle}>{section.title}</h2>
                <p className={styles.sectionContent}>{section.content}</p>
              </section>
            ))}
          </div>

          <footer className={styles.footer}>
            <Link to={`/gear?tab=${tabId}`} className={styles.backToGear}>
              ← {t('gear.title', 'Gear')} - {t(`gear.categories.${translateCategoryKey(category)}`, translateCategoryKey(category))}
            </Link>
          </footer>
        </article>
      </div>
    </>
  );
}

export default HowToChoosePage;
